import { defineStore } from 'pinia'
import Api from '@/config/api.helper'
import { useSessionStore } from '@/stores/components/sessionStore'

export interface NotificacoesEmpresa {
  notificacoes: NotificacoesDados[]
  pendencias: string[]
}

interface NotificacoesDados {
  notificacoesNaoLidas: number
  dados: {
    current_page: number
    first_page_url: string
    data: Notificacao[]
    from: number | null
    last_page: number | null
    last_page_url: string | null
    next_page_url: string | null
    path: string
    per_page: number
    prev_page_url: string | null
    to: number | null
    total: number
  }
}

export interface Notificacao {
  id: string
  type: string
  notifiable_type: string
  notifiable_id: number
  data: NotificacaoDados
  read_at: Date | null
  created_at: Date
  updated_at: Date
}

interface NotificacaoDados {
  titulo: string
  mensagem: string
  tipo: string
}

interface State {
  notificacoes: Notificacao[]
  notificacoesNaoLidas: number
  empresaListaStatus: string[]
  isLoading: boolean
  currentPage: number
  lastPage: number
  totalRecords: number
  rowsPerPage: number
}

export const useNotificacoesStore = defineStore('notificacoesStore', {
  state: (): State => {
    return {
      notificacoes: [],
      notificacoesNaoLidas: 0,
      empresaListaStatus: [],
      isLoading: false,
      currentPage: 1,
      lastPage: 1,
      totalRecords: 0,
      rowsPerPage: 10,
    }
  },

  actions: {
    async getNotificacoes() {
      if(this.isLoading) return
      this.isLoading = true
      try {
        const uuid = this.getUuidUsuario

        if (!uuid) throw new Error('Usuário não autenticado')

        const response = await Api.get(
          `empresas/notificacoes/${uuid}?page=${this.currentPage}`
        )

        this._salvarNotificacoesEmpresa(response.data.data)
      } catch (error) {
        console.error('Erro ao buscar notificações:', error)
      } finally {
        this.isLoading = false
      }
    },

    _salvarNotificacoesEmpresa(notificacoesEmpresa: NotificacoesEmpresa) {
      this.currentPage = notificacoesEmpresa.notificacoes.dados.current_page
      this.lastPage = notificacoesEmpresa.notificacoes.dados.last_page
      this.totalRecords = notificacoesEmpresa.notificacoes.dados.total
      this.rowsPerPage = notificacoesEmpresa.notificacoes.dados.per_page || 10
      this.empresaListaStatus = notificacoesEmpresa.pendencias

      this.notificacoesNaoLidas =
        notificacoesEmpresa.notificacoes.notificacoesNaoLidas +
        this.empresaListaStatus.length
      this.notificacoes = notificacoesEmpresa.notificacoes.dados.data
    },

    defineQtdNotificacoesNaoLidas(qtdNotificacoes: number) {
      this.notificacoesNaoLidas = qtdNotificacoes
    },

    addQtdNotificacoesNaoLidas(qtdNotificacoes: number) {
      this.notificacoesNaoLidas += qtdNotificacoes
    },

    removeQtdNotificacoesNaoLidas(qtdNotificacoes: number) {
      this.notificacoesNaoLidas -= qtdNotificacoes
    },

    defineNotificacoes(notificacoes: Notificacao[]) {
      this.notificacoes = notificacoes
    },

    defineNotificacoesEmpresa(notificacoesEmpresa: NotificacoesEmpresa) {
      this._salvarNotificacoesEmpresa(notificacoesEmpresa)
    },

    addNotificacao(notificacao: Notificacao) {
      let notificacaoExistente = this.notificacoes.find(
        (not) => not.id == not.id
      )
      if (notificacaoExistente) {
        notificacaoExistente = notificacao
      } else {
        this.notificacoes.push(notificacao)
      }
    },

    removeNotificacao(notificacaoId: Notificacao['id']) {
      this.notificacoes = this.notificacoes.filter(
        (not) => not.id != notificacaoId
      )
    },

    definePendencias(pendencias: string[]) {
      this.empresaListaStatus = pendencias
    },
  },

  getters: {
    getAllNotificacoes: (state) => [...state.notificacoes].sort((a, b) => {
      if(a.read_at && b.read_at){
        const dateA = new Date(a.read_at).getTime()
        const dateB = new Date(b.read_at).getTime()
        return dateB - dateA
      } else if(a.read_at){
        return 1
      } else if(b.read_at){
        return -1
      }
      return 0
    }),
    getNotificacoesNaoLidas: (state) => state.notificacoesNaoLidas,
    getUuidUsuario() {
      const sessionStore = useSessionStore()
      return sessionStore.userData?.usuario?.uuid_usu_id || null
    },
  },
})
